import * as React from 'react';
import {getLessons} from 'api/lesson/requests';
import {getCourses as getTeacherCourses} from 'api/course';
import {getCourses as getStudentCourses} from 'api/student/requests';
import {getWebinars} from 'api/webinar/requests';
import UserStore, {Role} from 'stores/UserStore';
import Modal, {ModalProps} from 'components/Modal';
import Instance from './components/Instance';
import Button, {ButtonVariant} from 'components/Button';
import validateResponse from 'helpers/validate-response';
import isError from 'helpers/isError';
import {ID, PreviewObject} from 'types';
import {IconLoading} from 'icons';
import {getPrograms} from '../../api/program/requests';
import {FoodItem, TrainingItem} from '../../api/program/types';

export enum InstanceType {
  Lesson = 'lesson',
  Course = 'course',
  Webinar = 'webinar',
  Training = 'gym',
  Food = 'ea',
}

export interface InstanceObject {
  id: ID;
  preview?: PreviewObject;
  title: string;
}

export type InstancePickerProps<T extends InstanceObject> = {
  type: InstanceType;
  multiply?: boolean;
  onSelect?(selected: T['id'][]): void;
  excludeId?: T['id'];
} & Pick<ModalProps, 'renderHeader' | 'onBlur'>;

/**
 * This component renders a modal window for selecting an instance.
 * @param {InstancePickerProps<T>} props
 * @return {React.ReactElement<InstancePickerProps<T>>}
 * @constructor
 */
export default function InstancePicker<T extends InstanceObject>(
    props: InstancePickerProps<T>,
): React.ReactElement<InstancePickerProps<T>> {
  const [
    isLoaded,
    setLoaded,
  ] = React.useState<boolean>(false);

  const [
    instances,
    setInstances,
  ] = React.useState<T[]>([]);

  const [
    attached,
    setAttached,
  ] = React.useState<T['id'][]>([]);

  React.useEffect(() => {
    switch (props.type) {
      case InstanceType.Lesson: {
        getLessons({
          my: '1',
        }).then((response) => {
          validateResponse(response);
          if (response && !isError(response)) {
            // todo: fix the instance generic type
            // @ts-ignore
            setInstances(response);
            setLoaded(true);
          }
        });
        break;
      }
      case InstanceType.Course: {
        switch (UserStore.state?.role) {
          case Role.Teacher: {
            getTeacherCourses({
              my: '1',
            }).then((response) => {
              validateResponse(response);
              if (response && !isError(response)) {
                // todo: fix the instance generic type
                // @ts-ignore
                setInstances(response);
                setLoaded(true);
              }
            });
            break;
          };
          case Role.Student: {
            getStudentCourses().then((response) => {
              validateResponse(response);
              if (response && !isError(response)) {
                // todo: fix the instance generic type
                // @ts-ignore
                setInstances(response);
                setLoaded(true);
              }
            });
            break;
          };
        }
        break;
      }
      case InstanceType.Webinar: {
        getWebinars({
          my: '1',
        }).then((response) => {
          validateResponse(response);
          if (response && !isError(response)) {
            // todo: fix the instance generic type
            // @ts-ignore
            setInstances(response);
            setLoaded(true);
          }
        });
        break;
      }
      case InstanceType.Training: {
        getPrograms<TrainingItem>({
          type: 'gym',
          my: '1',
        }).then((response) => {
          validateResponse(response);
          if (response && !isError(response)) {
            // todo: fix the instance generic type
            // @ts-ignore
            setInstances(response);
            setLoaded(true);
          }
        });
        break;
      }
      case InstanceType.Food: {
        getPrograms<FoodItem>({
          type: 'eat',
          my: '1',
        }).then((response) => {
          validateResponse(response);
          if (response && !isError(response)) {
            // todo: fix the instance generic type
            // @ts-ignore
            setInstances(response);
            setLoaded(true);
          }
        });
        break;
      }
    }
  }, []);

  /**
   * This function renders the instances.
   * @return {JSX.Element[]}
   */
  function renderInstances(): JSX.Element[] {
    /**
     * This function returns the status of the selected.
     * @param {T} instance
     * @return {boolean}
     */
    function isSelected(instance: T): boolean {
      return Boolean(attached.find((id) => (
        instance.id === id
      )));
    }

    /**
     * The select event handler.
     * @param {T} instance
     * @param {boolean} selected
     */
    function handleSelect(instance: T, selected: boolean): void {
      if (props.multiply) {
        if (selected) {
          setAttached([
            ...attached,
            instance['id'],
          ]);
        } else {
          setAttached(attached.filter((id) => (
            instance.id !== id
          )));
        }
      } else {
        if (selected) {
          setAttached([instance['id']]);
        } else {
          setAttached([]);
        }
      }
    }

    return instances.filter((instance) => (
      instance.id !== props.excludeId
    )).map((instance, index) => (
      <Instance
        key={index}
        {...instance}
        selected={isSelected(instance)}
        onSelect={(selected) => (
          handleSelect(instance, selected)
        )}
      />
    ));
  }

  /**
   * Thus function render the modal footer.
   * @return {JSX.Element}
   */
  function renderFooter(): JSX.Element {
    /**
     * This click event handler.
     */
    function handleClick(): void {
      props.onSelect && props.onSelect(attached);
    }

    return (
      <Button
        variant={ButtonVariant.Primary}
        disabled={!attached.length}
        onClick={handleClick}
      >
        Добавить ({attached.length})
      </Button>
    );
  }

  return (
    <Modal
      renderHeader={props.renderHeader}
      renderFooter={renderFooter()}
      onBlur={props.onBlur}
      fullScreen
    >
      {isLoaded ? (
        renderInstances()
      ) : (
        <IconLoading />
      )}
    </Modal>
  );
}
